import { Component, OnInit, ViewChild } from '@angular/core';
import { CommunicationService } from '../../services/communication/communication.service';
import { AchooPopupComponent } from '../../core/achoo-popup/achoo-popup.component';
import { ModalService } from '../../services/modal/modal.service';
import {Conversation, Message, ShortMessageStatus} from '../../../models/inbox';
import {UserService} from '../../services/user.service';
import {AbstractAchooComponent} from '../abstract-achoo.component';
import {DeviceDetectorService} from 'ngx-device-detector';
import {AppSettings} from '../../utils/app-settings';
import {Router} from '@angular/router';
import {DateUtils} from '../../utils/date.utils';
import {CommonConstant} from '../../app.constants';

@Component({
  selector: 'influencer-inbox',
  templateUrl: './influencer-inbox.component.html',
  styleUrls: ['./influencer-inbox.component.scss']
})
export class InfluencerInboxComponent extends AbstractAchooComponent implements OnInit {

  public searchContent: string;
  public showActivityLog: boolean | false;
  public conservationList: Conversation[];
  public editingMessages = false;
  public conversationToRemove: Conversation;
  public currentChatHost: any;
  public selectedConversation: Conversation;

  private startLoadingTime: Date;
  private currentOffset = 0;


  @ViewChild('confirmRemoveMessageDialog')
  confirmRemoveMessageDialog: AchooPopupComponent;

  constructor(private influencerInboxService: CommunicationService,
              public userService: UserService,
              private router: Router,
              private modalService: ModalService,
              private deviceService: DeviceDetectorService) {
    super(userService);
    this.showActivityLog = true;
    this.startLoadingTime = DateUtils.now();
  }

  ngOnInit() {
    // prevent user access to inbox when it's disabled
    if (AppSettings.isDisableChat()) {
      // in new influencer app
      if (this.userService.isInfluencer) {
        return this.router.navigate(['dashboard', 'influencer', 'details']);
      } else {
        return this.router.navigate(['discover']);
      }
    }
    super.ngOnInit();
  }

  companyChangeCallback(companyId) {
    this.conservationList = [];
    this.selectedConversation = null;
    this.currentOffset = 0;
    this.loadConservationList(companyId);
  }

  fetchNextConversation() {
    this.loadConservationList(this.companyId);
  }

  public editMessages(e: Event) {
    if (e) {
      e.preventDefault();
    }
    this.editingMessages = !this.editingMessages;
  }

  get unReadMessageCounting() {
    return (this.conservationList || []).filter((x) => {
      return x.status === 1;
    }).length;
  }

  public onConfirmRemoveConversation(con: Conversation) {
    if (!con) {
      return;
    }
    this.conversationToRemove = con;
    this.modalService.openInCenter(this.confirmRemoveMessageDialog);
  }

  public confirmBeforeRemovingConversation() {
    this.modalService.close();
    if (this.conversationToRemove) {
      this.influencerInboxService.deleteConversation(this.companyId, this.conversationToRemove.id).subscribe((res) => {
        this.conservationList = this.conservationList.filter(c => c.id !== this.conversationToRemove.id);
        // in case, delete the active conversation, set the first one as active
        if (this.conversationToRemove.id === this.selectedConversation.id) {
          if (this.conservationList.length > 0) {
            this.selectedConversation = this.conservationList[0];
            this.activateConversation(this.selectedConversation);
          } else {
            this.selectedConversation = null;
          }
        }
        this.conversationToRemove = null;
      });
    }
  }

  public closeConfirmRemoveConversation() {
    this.modalService.close();
  }

  public onSelectConversation(con: Conversation) {
    if (con && (!this.selectedConversation || (this.selectedConversation && con.id !== this.selectedConversation.id))) {
      this.activateConversation(con);
      const lastMessageId = con.messages.length > 0 ? con.messages[con.messages.length - 1].id : null;
      this.onReadLastMessages({
        conversationId: con.id,
        lastMessageId
      });
    }
  }

  public isAMessageSelected() {
    return !!this.selectedConversation;
  }

  public backToInbox() {
    this.selectedConversation = null;
  }

  get sortedConversationList() {
    if (!this.conservationList) {
      return [];
    }
    let sortedConversationList = this.conservationList;
    if (this.searchContent && this.searchContent !== '') {
      sortedConversationList = this.conservationList.filter(c => c.name.toLowerCase().indexOf(this.searchContent.toLowerCase()) >= 0);
    }
    return sortedConversationList;
  }

  /**
   * Callback after user read the new unreaded messages
   * @param conversationId
   */
  public onReadLastMessages(data) {
    // stop processing if missing required params
    if (!data.conversationId || !data.lastMessageId) {
      return;
    }
    this.influencerInboxService.markMessageAsSeen(this.userService.getCompanyId(), data.conversationId, data.lastMessageId)
      .subscribe((res) => {
        const con = this.conservationList.find(c => c.id === data.conversationId);
        if (con) {
          con.status = ShortMessageStatus.Normal;
        }
        // executed callback after marking the last message as seen
        if (data.callback) {
          data.callback();
        }
    });
  }

  /**
   * Callback after some new messages were loaded
   * @param conversationId
   */
  public newMessageAdded(data) {
    const con = this.conservationList.find(c => c.id === data.conversationId);
    if (con) {
      con.status = data.status;
      con.messages = [data.lastMessage];
      con.lastUpdated = data.lastMessage.sentTime;
    }
    this.conservationList.sort((c1, c2) => {
      return c2.lastUpdated > c1.lastUpdated ? 1 : c2.lastUpdated < c1.lastUpdated ? -1 : 0;
    });
  }

  public getActiveConversationName(con) {
    return this.influencerInboxService.normalizeConversationName(con);
  }

  private loadConservationList(companyId) {
    const isFirstPage = this.currentOffset === 0;
    this.influencerInboxService.fetchConservationList(companyId, this.currentOffset, this.startLoadingTime)
    .subscribe((res) => {
      this.conservationList = this.conservationList.concat(res.conversations);
      this.currentOffset = res.offset;
      // don't show the latest conversation on mobile
      if (this.deviceService.isMobile() ) {
        return;
      }
      if (this.selectedConversation) {
        this.onSelectConversation(this.selectedConversation);
      } else if (this.conservationList.length > 0) {
        this.onSelectConversation(this.conservationList[0]);
      }
    });
  }

  private activateConversation(conversation: Conversation) {
    // optimistic UI, show selected convos immediately after user select.
    // just waiting for API to update message and permission
    this.selectedConversation = Object.assign({}, conversation, {
      messages: [],
      permission: null
    });
    this.conservationList.forEach(convo => convo.isSelected = convo.id === conversation.id);
  }
}



// WEBPACK FOOTER //
// ./src/app/main/influencer-inbox/influencer-inbox.component.ts